import { Box } from '@mui/material'
import { C1Link } from 'components/core/links/C1Link'
import { Route } from 'lib/routes'
import { FC, RefObject } from 'react'

type FlyoutSafeAreaProps = {
    /** Ref to the nav item wrapper element */
    wrapperRef: RefObject<HTMLDivElement>
    /** Ref to the flyout content element */
    flyoutRef: HTMLDivElement | null
    /** Current mouse position in viewport coordinates */
    mousePosition: { x: number; y: number }
    /** Link href - if provided, renders as a link */
    href?: Route
}

/**
 * Renders a triangular "safe area" overlay between a nav item and its flyout.
 *
 * When moving the mouse diagonally from a nav item toward its flyout, the cursor
 * naturally passes over other nav items. Without this safe area, hovering those
 * items would trigger their hover states and close the original flyout.
 *
 * This component renders a triangle using fixed positioning that:
 * - Starts at the current cursor position (apex)
 * - Extends to cover the path toward the flyout
 * - Keeps the parent nav item's hover state active during diagonal movement
 *
 * The triangle is created using CSS clipPath with three vertices:
 * 1. Current mouse position (apex)
 * 2. Top-right corner of the safe area box (toward flyout top)
 * 3. Bottom-right corner of the safe area box (toward flyout bottom)
 */
export const FlyoutSafeArea: FC<FlyoutSafeAreaProps> = ({
    wrapperRef,
    flyoutRef,
    mousePosition,
    href,
}) => {
    const wrapperRect = wrapperRef.current?.getBoundingClientRect()
    const flyoutRect = flyoutRef?.getBoundingClientRect()

    if (!wrapperRect || !flyoutRect) return null

    // Safe area spans from nav item left edge to flyout left edge
    const left = wrapperRect.left
    const right = flyoutRect.left
    const width = right - left

    // Top is at the top of the flyout content (first clickable item)
    const top = flyoutRect.top

    // Bottom extends to whichever is lower: nav link bottom or flyout content bottom
    const bottom = Math.max(wrapperRect.bottom, flyoutRect.bottom)
    const height = bottom - top

    // Mouse position relative to the safe area box
    const pos = {
        x: mousePosition.x - left,
        y: mousePosition.y - top,
    }

    const baseSx = {
        position: 'fixed',
        left,
        top,
        width,
        height,
        clipPath: `polygon(${pos.x}px ${pos.y}px, 100% 0, 100% 100%)`,
        zIndex: 6000000,
        pointerEvents: 'auto',
    }

    // Render as a link if href is provided, so modifier keys work naturally
    if (href) {
        return <Box component={C1Link} href={href} sx={{ ...baseSx, cursor: 'pointer' }} />
    }

    return <Box sx={baseSx} />
}
